import React, { useMemo, useRef, useState } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBell,
  faBellSlash,
  faCheck,
  faClock,
  faCloudArrowUp,
  faExpand,
  faPlus,
  faRepeat,
  faSliders,
  faSnowflake,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import { adiarLembrete, alterarStatus } from "../../api/lembretesApi";
import { useLembretes } from "../../context/LembretesContext";
import { enfileirar, flush } from "../../services/captureQueue";
import PersonalizarLembreteModal from "../PersonalizarLembreteModal";
import {
  estaVencido,
  formatarLembrarEm,
  parseReminderInput,
} from "../../utils/reminderParser";
import {
  REMINDER_RECURRENCE,
  REMINDER_STATUS,
  ehRecorrente,
  getRecurrenceLabel,
} from "../../utils/reminderStatus";
import "./LembretesCard.css";

const LIMITE_VISIVEL = 5;

const LembretesCard = () => {
  const navigate = useNavigate();
  const {
    abertos,
    resumo,
    carregando,
    pendentesNaFila,
    recarregar,
    removerLocalmente,
    substituirLocalmente,
  } = useLembretes();

  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(null);

  const [novo, setNovo] = useState("");
  const [personalizado, setPersonalizado] = useState(null);
  const [mostrarPersonalizar, setMostrarPersonalizar] = useState(false);
  const inputRef = useRef(null);

  const analise = useMemo(() => parseReminderInput(novo), [novo]);
  const lembrarEm = analise.lembrarEm || personalizado?.lembrarEm || null;
  const recorrencia = analise.lembrarEm
    ? REMINDER_RECURRENCE.NENHUMA
    : personalizado?.recorrencia || REMINDER_RECURRENCE.NENHUMA;
  const textoNovo = (analise.texto || "").trim() || novo.trim();

  /** Captura sem sair do dashboard - a fila cuida do envio. */
  function capturar() {
    if (!textoNovo) return;

    enfileirar({ texto: textoNovo, lembrarEm, recorrencia });

    setNovo("");
    setPersonalizado(null);
    setErro("");
    inputRef.current?.focus();

    flush()
      .then(recarregar)
      .catch(() => {});
  }

  // Vencidos primeiro, depois os que tem hora, depois os sem alarme.
  const visiveis = useMemo(() => {
    const ordenados = [...abertos].sort((a, b) => {
      const aTem = Boolean(a.lembrar_em);
      const bTem = Boolean(b.lembrar_em);

      if (aTem !== bTem) return aTem ? -1 : 1;
      if (!aTem) return 0;

      return new Date(a.lembrar_em) - new Date(b.lembrar_em);
    });

    return ordenados.slice(0, LIMITE_VISIVEL);
  }, [abertos]);

  async function concluir(lembrete) {
    setOcupado(lembrete.id);
    setErro("");

    try {
      const atualizado = await alterarStatus(lembrete.id, REMINDER_STATUS.FEITO);

      // Recorrente nao sai da lista: o servidor devolve a proxima ocorrencia.
      if (atualizado && Number(atualizado.status) === REMINDER_STATUS.ABERTO) {
        substituirLocalmente(atualizado);
      } else {
        removerLocalmente(lembrete.id);
      }
    } catch (error) {
      console.error("LembretesCard: erro ao concluir", error);
      setErro(error?.response?.data?.message || "Nao foi possivel concluir o lembrete.");
      recarregar();
    } finally {
      setOcupado(null);
    }
  }

  async function adiar(lembrete) {
    setOcupado(lembrete.id);
    setErro("");

    try {
      const atualizado = await adiarLembrete(lembrete.id, 60);
      substituirLocalmente(atualizado);
    } catch (error) {
      console.error("LembretesCard: erro ao adiar", error);
      setErro(error?.response?.data?.message || "Nao foi possivel adiar o lembrete.");
      recarregar();
    } finally {
      setOcupado(null);
    }
  }

  return (
    <Card className="lembretes-card border-0 shadow-sm">
      <Card.Body>
        <div className="lembretes-card-header">
          <div>
            <h5>
              <FontAwesomeIcon icon={faBell} /> Lembretes
            </h5>
            <small>
              {resumo.abertos === 0
                ? "Caixa de entrada limpa"
                : `${resumo.abertos} aberto(s)${
                    resumo.vencidos > 0 ? ` · ${resumo.vencidos} vencido(s)` : ""
                  }`}
            </small>
          </div>

          <button
            type="button"
            className="lembretes-card-novo"
            onClick={() => navigate("/capturar")}
            title="Abrir tela de captura (para despejar varios)"
            aria-label="Abrir tela de captura"
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>

        {/* Campo sempre aberto: capturar daqui nao custa nenhuma navegacao. */}
        <div className="lembretes-card-captura">
          <div className="lembretes-card-campo">
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              placeholder="Anotar um lembrete..."
              value={novo}
              onChange={(evento) => setNovo(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter") {
                  evento.preventDefault();
                  capturar();
                }
              }}
            />

            <button
              type="button"
              className="lembretes-card-enviar"
              onClick={capturar}
              disabled={!textoNovo}
              title="Adicionar"
              aria-label="Adicionar lembrete"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          <div className="lembretes-card-quando">
            <button
              type="button"
              className={`lembretes-card-personalizar ${personalizado ? "ativo" : ""}`}
              onClick={() => setMostrarPersonalizar(true)}
              disabled={Boolean(analise.lembrarEm)}
            >
              <FontAwesomeIcon icon={faSliders} /> Personalizar
            </button>

            <span className={lembrarEm ? "com-alarme" : ""}>
              <FontAwesomeIcon icon={lembrarEm ? faBell : faBellSlash} />{" "}
              {lembrarEm ? formatarLembrarEm(lembrarEm) : "Sem alarme"}
              {ehRecorrente(recorrencia) && ` · ${getRecurrenceLabel(recorrencia)}`}
            </span>
          </div>
        </div>

        {erro && <Alert variant="warning" className="mt-3 mb-0 py-2">{erro}</Alert>}

        {pendentesNaFila > 0 && (
          <div className="lembretes-card-fila">
            <FontAwesomeIcon icon={faCloudArrowUp} /> {pendentesNaFila} aguardando envio
          </div>
        )}

        {carregando && abertos.length === 0 ? (
          <div className="lembretes-card-carregando">
            <Spinner size="sm" />
          </div>
        ) : visiveis.length === 0 ? (
          <div className="lembretes-card-vazio">
            <FontAwesomeIcon icon={faCheck} />
            <strong>Nada esquecido por aqui.</strong>
            <span>Capture algo antes que escape.</span>
          </div>
        ) : (
          <div className="lembretes-card-lista">
            {visiveis.map((lembrete) => {
              const vencido = estaVencido(lembrete.lembrar_em);
              const travado = ocupado === lembrete.id;

              return (
                <article
                  key={lembrete.id}
                  className={`lembretes-card-item ${vencido ? "vencido" : ""} ${
                    travado ? "ocupado" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="lembretes-card-check"
                    onClick={() => concluir(lembrete)}
                    disabled={travado}
                    title="Marcar como feito"
                    aria-label={`Concluir ${lembrete.texto}`}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>

                  <div className="lembretes-card-conteudo">
                    <strong>{lembrete.texto}</strong>

                    <div className="lembretes-card-meta">
                      {lembrete.lembrar_em ? (
                        <span className={vencido ? "vencida" : ""}>
                          <FontAwesomeIcon
                            icon={vencido ? faTriangleExclamation : faClock}
                          />{" "}
                          {formatarLembrarEm(lembrete.lembrar_em)}
                        </span>
                      ) : (
                        <span className="sem-hora">Sem alarme</span>
                      )}

                      {ehRecorrente(lembrete.recorrencia) && (
                        <span className="recorrente">
                          <FontAwesomeIcon icon={faRepeat} />{" "}
                          {getRecurrenceLabel(lembrete.recorrencia)}
                        </span>
                      )}
                    </div>
                  </div>

                  {vencido && (
                    <button
                      type="button"
                      className="lembretes-card-adiar"
                      onClick={() => adiar(lembrete)}
                      disabled={travado}
                      title="Adiar 1 hora"
                      aria-label="Adiar 1 hora"
                    >
                      <FontAwesomeIcon icon={faSnowflake} />
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {abertos.length > LIMITE_VISIVEL && (
          <button
            type="button"
            className="lembretes-card-todos"
            onClick={() => navigate("/lembretes")}
          >
            Ver todos os {abertos.length} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        )}

        <PersonalizarLembreteModal
          show={mostrarPersonalizar}
          valorInicial={personalizado}
          onHide={() => setMostrarPersonalizar(false)}
          onExited={() => inputRef.current?.focus()}
          onConfirm={(escolha) => {
            setPersonalizado(escolha.lembrarEm ? escolha : null);
            setMostrarPersonalizar(false);
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default LembretesCard;
